import React, { useState, useEffect, useMemo } from 'react';
import { Card, Form, Button, Space, message } from 'antd';
import { PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import supplyChainService, { Supplier, Warehouse } from '../../../services/supplyChainService';
import dayjs from 'dayjs';
import api from '../../../services/api';
import { POHeaderForm } from './components/POHeaderForm';
import { POItemsTable } from './components/POItemsTable';

interface POItem {
    id?: number;
    internal_sku: number;
    sku_code?: string;
    sku_name?: string;
    quantity: number;
    unit_price: number;
    tax_rate: number;
    remark?: string;
    total_price?: number;
}

const PurchaseOrderDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = !id || id === 'new';

    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [skus, setSkus] = useState<any[]>([]);
    const [poItems, setPoItems] = useState<POItem[]>([]);
    const [currentStatus, setCurrentStatus] = useState('draft');

    const fetchData = async () => {
        try {
            const [supRes, whRes, skuRes] = await Promise.all([
                supplyChainService.getSuppliers({ page_size: 1000 }),
                supplyChainService.getWarehouses({ page_size: 1000 }),
                api.get('/skus/?page_size=1000'),
            ]);
            setSuppliers((supRes.data as any).results || supRes.data || []);
            setWarehouses((whRes.data as any).results || whRes.data || []);
            setSkus((skuRes.data as any).results || []);

            if (!isNew) {
                const poRes = await supplyChainService.getPurchaseOrder(parseInt(id));
                const po = poRes.data;
                form.setFieldsValue({
                    ...po,
                    order_date: po.order_date ? dayjs(po.order_date) : null,
                    expected_arrival_date: po.expected_arrival_date ? dayjs(po.expected_arrival_date) : null,
                });
                setPoItems(po.items || []);
                setCurrentStatus(po.status);
            } else {
                form.setFieldsValue({
                    status: 'draft', order_date: dayjs(),
                    po_number: `PO${dayjs().format('YYYYMMDDHHmm')}`, currency: 'CNY'
                });
            }
        } catch { message.error('加载详情失败'); }
    };

    useEffect(() => { fetchData(); }, [id]);

    const handleAddItem = () => setPoItems([...poItems, { internal_sku: 0, quantity: 1, unit_price: 0, tax_rate: 0 }]);
    const handleRemoveItem = (index: number) => {
        const newItems = [...poItems];
        newItems.splice(index, 1);
        setPoItems(newItems);
    };

    const updateItem = (index: number, field: keyof POItem, value: any) => {
        const newItems = [...poItems];
        newItems[index] = { ...newItems[index], [field]: value };
        if (field === 'internal_sku') {
            const selectedSku = skus.find(s => s.id === value);
            if (selectedSku) {
                newItems[index].sku_code = selectedSku.sku_code;
                newItems[index].sku_name = selectedSku.name;
                newItems[index].unit_price = selectedSku.purchase_cost_cny || 0;
            }
        }
        setPoItems(newItems);
    };

    const totalAmount = useMemo(() => poItems.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0), [poItems]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (!poItems.length) return message.warning('请添加至少一项采购明细');
            if (poItems.some(i => !i.internal_sku)) return message.warning('请确保所有明细都选择了 SKU');

            setSubmitting(true);
            const payload = {
                ...values, total_amount: totalAmount, items: poItems,
                order_date: values.order_date?.format('YYYY-MM-DD'),
                expected_arrival_date: values.expected_arrival_date?.format('YYYY-MM-DD'),
            };
            if (isNew) {
                const res = await supplyChainService.createPurchaseOrder(payload);
                message.success('创建成功');
                navigate(`/supply-chain/procurement/orders/${res.data.id}`);
            } else {
                await supplyChainService.updatePurchaseOrder(parseInt(id), payload);
                message.success('保存成功');
                fetchData();
            }
        } catch { message.error('操作失败'); }
        finally { setSubmitting(false); }
    };

    const handleReceive = async () => {
        try {
            setSubmitting(true);
            await supplyChainService.receivePurchaseOrder(parseInt(id!));
            message.success('入库成功');
            fetchData();
        } catch (error: any) { message.error(error.response?.data?.error || '入库失败'); }
        finally { setSubmitting(false); }
    };

    const handleApprove = async () => {
        try {
            setSubmitting(true);
            await supplyChainService.approvePurchaseOrder(parseInt(id!));
            message.success('审核通过');
            fetchData();
        } catch { message.error('审核失败'); }
        finally { setSubmitting(false); }
    };

    return (
        <div style={{ padding: '0 0 24px 0' }}>
            <Space style={{ marginBottom: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/supply-chain/procurement/orders')}>返回列表</Button>
            </Space>

            <Form form={form} layout="vertical">
                <POHeaderForm
                    isNew={isNew} currentStatus={currentStatus} suppliers={suppliers} warehouses={warehouses}
                    submitting={submitting} onSave={handleSave} onApprove={handleApprove} onReceive={handleReceive}
                    poNumber={form.getFieldValue('po_number')}
                />
                <div style={{ marginTop: 24 }}>
                    <Card
                        title="采购明细"
                        extra={currentStatus !== 'received' && (
                            <Button type="dashed" onClick={handleAddItem} icon={<PlusOutlined />}>添加产品</Button>
                        )}
                    >
                        <POItemsTable
                            items={poItems} skus={skus} currentStatus={currentStatus}
                            onUpdateItem={updateItem} onRemoveItem={handleRemoveItem} totalAmount={totalAmount}
                        />
                    </Card>
                </div>
            </Form>
        </div>
    );
};

export default PurchaseOrderDetail;
